// JIRA: LOGISPRD-15411
import { useEffect, useMemo, useState } from 'react'
import styled from 'styled-components'
import SelectBox from '@/components/content/SelectBox'
import SearchableSelectBox from '@/components/content/SearchableSelectBox'
import { buildShipperNameOptions } from '@/components/system/shipperSelectFilter'
import { SHIPPER_TYPES } from '@/services/shipper/apis'
import { useGetShipperOptions } from '@/services/shipper/useShipperQueries'

const TYPE_OPTIONS = SHIPPER_TYPES.map((type) => ({ value: type, label: type }))

/** 셀렉트 너비(px) — 기존 ShipperSelector 의 유형·화주명 칸과 같은 값 */
const TYPE_SELECT_WIDTH = 100
const SHIPPER_SELECT_WIDTH = 250

interface ShipperCodeSelectProps {
  /** 화주코드. 빈 값은 전체 */
  value?: string
  onChange: (shipperCode: string) => void
}

/**
 * 화주유형 + 화주 셀렉트 — 값은 화주코드 하나다. IV02·IV03·RP06 공용(1.0 화주 선택 모달 대체).
 *
 * 화주유형은 화주 목록을 좁히는 데만 쓰고 조회 조건에는 싣지 않는다(BE 는 화주코드 하나만 받는다).
 * 그래서 유형을 고르면 화주를 그 유형의 화주로 채운다 — 비워 두면 "유형 MWP" 로 보이는 채 모든 화주로 조회된다.
 * "전체" 는 유형도 전체일 때만 고를 수 있다.
 */
function ShipperCodeSelect({ value = '', onChange }: ShipperCodeSelectProps) {
  const [serviceType, setServiceType] = useState('')
  const { data: shippers = [] } = useGetShipperOptions()

  const options = useMemo(() => {
    const built = buildShipperNameOptions(shippers, { valueKey: 'shipperCode', serviceTypes: [serviceType] })
    // URL 복원값이 목록에 없으면(비활성 화주 등) 코드라도 보인다 — 빼면 값은 실린 채 칸이 비어 보인다.
    return value && !built.some((o) => o.value === value) ? [{ value, label: value }, ...built] : built
  }, [shippers, serviceType, value])

  // 이 화면들은 sprNm 에 화주명을 담던 때가 있었다(옛 URL 북마크). 이름이 한 화주로 맞으면 코드로 바꾼다 —
  // 그대로 두면 이름이 코드 자리에 실려 0행인데 화면은 정상 선택처럼 보인다. 이름이 겹치면 고르지 않는다.
  useEffect(() => {
    if (!value || shippers.some((s) => s.shipperCode === value)) return
    const byName = shippers.filter((s) => s.shipperName === value)
    if (byName.length === 1) onChange(byName[0].shipperCode)
    // eslint-disable-next-line react-hooks/exhaustive-deps -- onChange 는 부모가 매 렌더 새로 만든다
  }, [value, shippers])

  // 초기화·복원으로 값이 밖에서 바뀌어 고른 유형에 없는 화주가 되면 유형을 푼다 — 남기면 "MWP + MUSINSA" 처럼
  // 있을 수 없는 조합이 보인다. 유형은 폼 값이 아니라 reset 이 닿지 않는다.
  // 유형 변경에는 반응하지 않는다 — 사용자가 막 고른 유형을, 부모가 값을 비우기 전 렌더에서 되돌리면 안 된다.
  // 값이 비면(초기화가 빈 값을 심는 등) 유형도 푼다 — 빈 값은 모든 화주로 조회된다.
  useEffect(() => {
    const own = value ? shippers.find((s) => s.shipperCode === value) : undefined
    setServiceType((type) => (type && own?.logisticsServiceType !== type ? '' : type))
  }, [value, shippers])

  return (
    <Container>
      <TypeWrap data-testid="shipper-type">
        <SelectBox<string>
          block
          hasAll
          placeholder="화주유형"
          value={serviceType}
          options={TYPE_OPTIONS}
          onChange={(type) => {
            if (!type) return setServiceType('')
            const fits = buildShipperNameOptions(shippers, { valueKey: 'shipperCode', serviceTypes: [type] })
            // 그 유형의 화주가 없으면(목록 로딩 전·조회 실패 포함) 유형을 적용하지 않는다 — 화주를 비우면 모든 화주로 조회된다.
            if (fits.length === 0) return
            setServiceType(type)
            if (!fits.some((o) => o.value === value)) onChange(fits[0].value)
          }}
        />
      </TypeWrap>
      <ShipperWrap>
        <SearchableSelectBox<string> block hasAll={!serviceType} placeholder="화주" value={value} options={options} onChange={onChange} />
      </ShipperWrap>
    </Container>
  )
}

const Container = styled.div`
  display: flex;
  gap: 8px;
  align-items: center;
`

const TypeWrap = styled.div`
  width: ${TYPE_SELECT_WIDTH}px;
`

const ShipperWrap = styled.div`
  width: ${SHIPPER_SELECT_WIDTH}px;
`

export default ShipperCodeSelect
